/**
 * Singleton AudioContext — se crea una sola vez y se reutiliza.
 * Los navegadores modernos bloquean AudioContext si no viene de gesto del usuario,
 * por eso se desbloquea al primer click con inicializarAudio().
 */
let _ctx = null;

function getCtx() {
  if (!_ctx) {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return _ctx;
}

/**
 * Llamar en el primer gesto del usuario (click/touchstart) para desbloquear el audio.
 * Layout.jsx lo llama con { once: true }.
 */
export function inicializarAudio() {
  try {
    const ctx = getCtx();
    if (ctx.state === 'suspended') ctx.resume();
  } catch {
    // Sin soporte — ignorar
  }
}

/**
 * Reproduce un sonido de sirena de emergencia usando Web Audio API.
 * @param {'critica'|'alta'|'media'|'baja'} prioridad
 * @param {number} duracionSeg - Duración total en segundos (default 25)
 */
export function reproducirAlerta(prioridad = 'media', duracionSeg = 25) {
  try {
    const ctx = getCtx();

    const configs = {
      critica: { freqLow: 620, freqHigh: 1150, durCiclo: 0.30, vol: 0.72 },
      alta:    { freqLow: 520, freqHigh:  950, durCiclo: 0.38, vol: 0.62 },
      media:   { freqLow: 420, freqHigh:  720, durCiclo: 0.45, vol: 0.50 },
      baja:    { freqLow: 360, freqHigh:  560, durCiclo: 0.55, vol: 0.38 },
    };

    const c = configs[prioridad] || configs.media;
    const cicloTotal = c.durCiclo * 2;
    const ciclos     = Math.ceil(duracionSeg / cicloTotal);

    const play = () => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'sawtooth';

      const start = ctx.currentTime + 0.05;

      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(c.vol, start + 0.08);
      gain.gain.setValueAtTime(c.vol, start + duracionSeg - 0.4);
      gain.gain.linearRampToValueAtTime(0, start + duracionSeg);

      let t = start;
      for (let i = 0; i < ciclos; i++) {
        osc.frequency.setValueAtTime(c.freqLow, t);
        osc.frequency.linearRampToValueAtTime(c.freqHigh, t + c.durCiclo);
        osc.frequency.linearRampToValueAtTime(c.freqLow, t + cicloTotal);
        t += cicloTotal;
      }

      osc.start(start);
      osc.stop(start + duracionSeg);
    };

    // Si el contexto ya está activo, reproducir de inmediato; si no, resumirlo primero
    if (ctx.state === 'running') {
      play();
    } else {
      ctx.resume().then(play).catch(() => {});
    }
  } catch {
    // Navegador sin soporte de Web Audio — silencio
  }
}



